import { createContext, useContext, type ReactNode } from 'react';
import type { SiteInfo } from '../seo/jsonld.js';
import type { SsrPayload } from './serialize.js';

const SsrDataContext = createContext<SsrPayload | null>(null);

/**
 * Deja disponible el estado que resolvió el loader en el server.
 * En `entry-server` recibe el resultado del loader; en `entry-client`, lo que vino
 * serializado en el HTML (`window.__SSR_DATA__`).
 */
export function SsrDataProvider({
  value,
  children,
}: {
  value: SsrPayload | null;
  children: ReactNode;
}) {
  return <SsrDataContext.Provider value={value}>{children}</SsrDataContext.Provider>;
}

/**
 * Datos precargados para una ruta, o `null` si esta página no se renderizó en server
 * (navegación client-side) — en ese caso el ViewModel carga como cualquier SPA.
 *
 * Patrón obligatorio de toda página pública:
 * ```tsx
 * const initial = useSsrData<HomeData>(HOME_ROUTE_KEY);
 * const vm = useViewModel(() => new HomeViewModel(initial));
 * ```
 */
export function useSsrData<T>(routeKey: string): T | null {
  const payload = useContext(SsrDataContext);
  if (!payload || payload.routeKey !== routeKey) return null;
  return payload.data as T;
}

/**
 * El payload crudo, sin filtrar por ruta. Lo usa el layout para leer lo que **toda** página
 * pública trae por convención (`shell`, `ads`) sin conocer la ruta activa.
 */
export function useSsrPayload(): SsrPayload | null {
  return useContext(SsrDataContext);
}

/**
 * Identidad del sitio (nombre + origen del request), para lo que se arma con la URL
 * absoluta y se dibuja en el cliente: los links de compartir y el `<head>` al navegar.
 *
 * Sale del payload —no de `Tenant.domain`, que es opcional y no conoce ni el esquema ni el
 * túnel— así que el server y el cliente renderizan lo mismo. No está filtrado por ruta: vale
 * en cualquier pantalla.
 *
 * El fallback a `location.origin` cubre solo la página que **no** vino del server (sin
 * payload). Ahí no hay HTML que hidratar, así que no puede haber mismatch.
 */
export function useSitio(): SiteInfo {
  const payload = useContext(SsrDataContext);
  if (payload) return payload.site;
  return { name: '', url: typeof window === 'undefined' ? '' : window.location.origin };
}
